import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { BULK_DELETE_DONATION_LIMIT } from '../../../../../queries/Donation/useBulkDeleteDonations';
import { Button } from '../../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../components-v2/Dialog';
import { Input } from '../../../../components-v2/Input';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { styleSheet } from './styles';

const CONFIRMATION_WORD = 'DELETE';

export const DeleteDonationsConfirmModal = ({
	isOpen,
	onClose,
	onDeleted,
	count,
	onConfirm,
}: {
	isOpen: boolean;
	onClose: () => void;
	onDeleted?: () => void | Promise<void>;
	count: number;
	onConfirm: () => Promise<number>;
}) => {
	const [typed, setTyped] = React.useState('');
	const [isDeleting, setIsDeleting] = React.useState(false);

	React.useEffect(() => {
		if (!isOpen) {
			setTyped('');
			setIsDeleting(false);
		}
	}, [isOpen]);

	const overLimit = count > BULK_DELETE_DONATION_LIMIT;
	const canConfirm = typed.trim().toUpperCase() === CONFIRMATION_WORD && count > 0 && !overLimit && !isDeleting;

	const confirm = async () => {
		if (!canConfirm) return;
		setIsDeleting(true);
		try {
			const deleted = await onConfirm();
			await onDeleted?.();
			toast.success(`Archived ${deleted} donation${deleted === 1 ? '' : 's'}`);
			onClose();
		} catch (err) {
			const apiErr = err as { message?: string };
			const message = apiErr?.message || 'Failed to delete donations. Please try again.';
			toast.error(message);
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<Dialog
			open={isOpen}
			dismissOnOutsideClick={!isDeleting}
			onOpenChange={open => {
				if (!open && !isDeleting) onClose();
			}}
		>
			<DialogPopup styles={[styleSheet.popup]} showCloseButton={!isDeleting}>
				<DialogHeader>
					<DialogTitle styles={[styleSheet.title]}>Delete donations?</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styleSheet.body)}>
						This will delete{' '}
						<span className={css(styleSheet.count)}>
							{count} donation{count === 1 ? '' : 's'}
						</span>
						. Donations are archived, not physically removed — contact support if you need to restore any.
					</div>
					{overLimit ? (
						<div className={css(styleSheet.body)}>
							<strong>That&apos;s over the {BULK_DELETE_DONATION_LIMIT.toLocaleString()}-row limit.</strong> Narrow the
							selection or filter and try again.
						</div>
					) : (
						<div className={css(styleSheet.inputWrap)}>
							<label className={css(styleSheet.label)} htmlFor='delete-donations-confirm-input'>
								Type <strong>{CONFIRMATION_WORD}</strong> to confirm
							</label>
							<Input
								id='delete-donations-confirm-input'
								type='text'
								value={typed}
								onChange={e => setTyped(e.target.value)}
								disabled={isDeleting}
							/>
						</div>
					)}
				</DialogBody>
				<DialogFooter>
					{isDeleting ? (
						<LoadingSpinner type='small' />
					) : (
						<>
							<Button variant='secondary' onClick={onClose}>
								Cancel
							</Button>
							<Button variant='destructive' onClick={confirm} disabled={!canConfirm}>
								Delete
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
